<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import axios from 'axios';
import {
  channels,
  config,
  normalizePhone,
  number,
  planKeys,
  calculatePrice,
} from '../config';

const props = defineProps({
  selection: {
    type: Object,
    default: () => ({
      plan: 'free',
      users: 2,
      accounts: 1,
      messages: 0,
      storage: 0.5,
    }),
  },
});
const { t } = useI18n();
const form = reactive({
  name: '',
  phone: '',
  business: '',
  email: '',
  plan: props.selection.plan,
  users: props.selection.users,
  accounts: props.selection.accounts,
  messages: props.selection.messages,
  storage: props.selection.storage,
  channels: [],
  notes: '',
  consent: false,
  website: '',
  source: 'landing',
});
const submitting = ref(false);
const error = ref('');
const success = ref('');
let requestId = crypto.randomUUID();
let lastPayload = '';
const estimate = computed(() =>
  calculatePrice(
    form.plan,
    form.users,
    form.accounts,
    form.messages,
    form.storage
  )
);
watch(
  () => props.selection,
  ({ plan, users, accounts, messages, storage }) => {
    form.plan = plan;
    form.users = users;
    form.accounts = accounts;
    form.messages = messages;
    form.storage = storage;
  }
);
watch(
  () => form.plan,
  plan => {
    if (plan === 'free') form.users = Math.min(form.users, 2);
  }
);

async function submit() {
  error.value = '';
  if (
    !form.name.trim() ||
    !/^\+989\d{9}$/.test(normalizePhone(form.phone)) ||
    !form.consent
  ) {
    error.value = t('form.invalid');
    return;
  }
  const payload = { ...form, phone: normalizePhone(form.phone) };
  const serialized = JSON.stringify(payload);
  if (lastPayload && lastPayload !== serialized)
    requestId = crypto.randomUUID();
  lastPayload = serialized;
  submitting.value = true;
  try {
    const { data } = await axios.post(
      '/api/activation',
      { ...payload, request_id: requestId },
      {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
            .content,
        },
      }
    );
    if (data.status !== 'received' || !data.reference)
      throw new Error('Invalid response');
    success.value = t('form.success', { reference: data.reference });
  } catch (failure) {
    const status = failure.response?.status;
    if (status === 409) requestId = crypto.randomUUID();
    const key =
      { 409: 'conflict', 422: 'invalid_request', 429: 'rateLimited' }[status] ||
      'failed';
    error.value = t(`form.${key}`);
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div
    class="rounded-3xl border border-n-slate-4 bg-white p-6 shadow-xl shadow-n-slate-4/40 md:p-9"
  >
    <div
      v-if="success"
      role="status"
      class="rounded-2xl bg-n-accent/10 p-6 leading-9 text-n-slate-12"
    >
      {{ success }}
    </div>
    <form v-else class="space-y-5" @submit.prevent="submit">
      <div class="grid gap-5 sm:grid-cols-2">
        <label class="text-sm font-medium"
          >{{ t('form.name')
          }}<input
            v-model="form.name"
            name="name"
            autocomplete="name"
            maxlength="100"
            required
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:bg-white focus:ring-2 focus:ring-woot-600"
        /></label>
        <label class="text-sm font-medium"
          >{{ t('form.phone')
          }}<input
            v-model="form.phone"
            name="phone"
            type="tel"
            dir="ltr"
            autocomplete="tel"
            maxlength="16"
            required
            aria-describedby="phone-hint"
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:bg-white focus:ring-2 focus:ring-woot-600"
          /><span id="phone-hint" class="mt-2 block text-xs text-n-slate-8">{{
            t('form.phoneHint')
          }}</span></label
        >
      </div>
      <label class="block text-sm font-medium"
        >{{ t('form.business')
        }}<input
          v-model="form.business"
          name="organization"
          autocomplete="organization"
          maxlength="150"
          class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:bg-white focus:ring-2 focus:ring-woot-600"
      /></label>
      <label class="block text-sm font-medium"
        >{{ t('form.email')
        }}<input
          v-model="form.email"
          name="email"
          type="email"
          autocomplete="email"
          maxlength="254"
          class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:bg-white focus:ring-2 focus:ring-woot-600"
      /></label>
      <div class="grid grid-cols-2 gap-4">
        <label class="text-sm font-medium"
          >{{ t('pricing.planLabel')
          }}<select
            v-model="form.plan"
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:ring-2 focus:ring-woot-600"
          >
            <option
              v-for="key in planKeys.filter(key => key !== 'unsure')"
              :key="key"
              :value="key"
            >
              {{ t(`pricing.${key}`) }}
            </option>
          </select></label
        >
        <label class="text-sm font-medium"
          >{{ t('pricing.usersLabel')
          }}<input
            v-model.number="form.users"
            type="number"
            min="1"
            :max="form.plan === 'free' ? 2 : config.max_users"
            required
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:ring-2 focus:ring-woot-600"
        /></label>
      </div>
      <div class="grid gap-4 sm:grid-cols-3">
        <label class="text-sm font-medium"
          >{{ t('pricing.accountsLabel')
          }}<input
            v-model.number="form.accounts"
            type="number"
            min="1"
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3"
        /></label>
        <label class="text-sm font-medium"
          >{{ t('pricing.messagesLabel')
          }}<input
            v-model.number="form.messages"
            type="number"
            min="0"
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3"
        /></label>
        <label class="text-sm font-medium"
          >{{ t('pricing.storageLabel')
          }}<input
            v-model.number="form.storage"
            type="number"
            min="0"
            step="0.5"
            class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3"
        /></label>
      </div>
      <p v-if="estimate" class="text-sm text-woot-600">
        {{ t('pricing.estimate') }}:
        {{
          estimate === null
            ? t('pricing.inquiry')
            : estimate.storageInquiry
              ? `${number(estimate.totalWithoutStorage)} ${t('pricing.totalWithoutStorage')}`
              : `${number(estimate.total)} ${t('pricing.month')}`
        }}
      </p>
      <fieldset>
        <legend class="mb-3 text-sm font-medium">
          {{ t('form.channels') }}
        </legend>
        <div class="flex flex-wrap gap-2">
          <label
            v-for="channel in channels.filter(c =>
              config.channels.includes(c.id)
            )"
            :key="channel.id"
            class="flex cursor-pointer items-center gap-2 rounded-lg border border-n-slate-4 px-3 py-2 text-xs"
            ><input
              v-model="form.channels"
              type="checkbox"
              :value="channel.id"
              class="accent-woot-600"
            />{{ t(`channels.${channel.id}`) }}</label
          >
        </div>
      </fieldset>
      <label class="block text-sm font-medium"
        >{{ t('form.notes')
        }}<textarea
          v-model="form.notes"
          maxlength="2000"
          rows="3"
          class="mt-2 w-full rounded-xl border border-n-slate-4 bg-n-slate-2 p-3 focus:bg-white focus:ring-2 focus:ring-woot-600"
        />
      </label>
      <input
        v-model="form.website"
        type="text"
        name="website"
        tabindex="-1"
        autocomplete="off"
        aria-hidden="true"
        class="hidden"
      />
      <label class="flex items-start gap-3 text-xs leading-6 text-n-slate-11"
        ><input
          v-model="form.consent"
          type="checkbox"
          required
          class="mt-1 shrink-0 accent-woot-600"
        />{{ t('form.consent') }}</label
      >
      <p
        v-if="error"
        role="alert"
        class="rounded-lg bg-red-50 p-3 text-sm leading-7 text-red-700"
      >
        {{ error }}
      </p>
      <button
        type="submit"
        :disabled="submitting"
        :aria-busy="submitting"
        class="w-full rounded-xl bg-woot-600 px-5 py-4 text-sm font-bold text-white hover:bg-woot-700 focus-visible:ring-2 focus-visible:ring-woot-600 focus-visible:ring-offset-4 disabled:cursor-wait disabled:opacity-60"
      >
        {{ submitting ? t('form.loading') : t('form.submit') }}
        <span class="ms-3">{{ t('arrow') }}</span>
      </button>
      <p class="text-center text-xs leading-6 text-n-slate-8">
        {{ t('form.notice') }}
      </p>
    </form>
  </div>
</template>
